import { useState } from "react";
import { Box, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { UseFormReturn } from "react-hook-form";
import { GroupsTitle } from "../../components/groups/GroupsTitle";
import { Group } from "../../utils/formatGroups";
import { GroupsOptions } from "../../types/groups.types";
import { DynamicTasksGroupBlock } from "./DynamicTasksGroupBlock";
import { attributionFields, hasValue, mergeFieldExtraOptions } from "./dynamicTasksOptions";

type AttributionType = "dynamic" | "fixed";

type AttributionColumnProps = {
	group: Group;
	item: any;
	type: string;
	entityForm?: UseFormReturn;
	groupsOptions?: GroupsOptions;
};

// The attribution group as a Dinâmico/Fixo switcher: the toggle (left) reveals only the selected
// attribution field (right); switching clears the now-hidden field. Any other field in the group
// (e.g. participant_users) renders below the switcher, not inside it.
export const AttributionColumn = ({ group, item, type, entityForm, groupsOptions }: AttributionColumnProps) => {
	const initialType: AttributionType = hasValue(item?.assigned_users) ? "fixed" : "dynamic";
	const [attributionType, setAttributionType] = useState<AttributionType>(initialType);

	const createdUserField = group.fields.find((field) => field.field === "dynamic_created_user");
	const createdUserGroup: Group = { groupId: "__created_user", groupName: "", fields: createdUserField ? [createdUserField] : [] };

	const selectedFieldName = attributionType === "dynamic" ? "dynamic_assigned_user" : "assigned_users";
	const selectedField = group.fields.find((field) => field.field === selectedFieldName);
	const switcherGroup: Group = {
		groupId: "__attribution",
		groupName: "",
		fields: selectedField ? [selectedField] : []
	};

	// Below the switcher: the rest of the group's fields, with both attribution fields hidden (the
	// selected one is shown in the switcher row).
	const restOptions: GroupsOptions = {
		...groupsOptions,
		fieldsWithExtraOptions: mergeFieldExtraOptions(
			groupsOptions?.fieldsWithExtraOptions ?? [],
			Object.fromEntries(
				[...attributionFields, "dynamic_created_user"].map((field) => [field, { hidden: { create: true, update: true } }])
			)
		)
	};

	const handleChange = (_event: React.MouseEvent<HTMLElement>, newType: AttributionType | null) => {
		if (!newType || newType === attributionType) return;
		setAttributionType(newType);
		const fieldToClear = newType === "dynamic" ? "assigned_users" : "dynamic_assigned_user";
		entityForm?.setValue(fieldToClear, null, { shouldDirty: true });
	};

	return (
		<>
			<GroupsTitle title={group.groupName} />
			{createdUserField && (
				<DynamicTasksGroupBlock group={createdUserGroup} item={item} type={type} entityForm={entityForm} groupsOptions={groupsOptions} hideTitle />
			)}
			<Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
				<ToggleButtonGroup
					color="secondary"
					exclusive
					size="small"
					orientation="vertical"
					value={attributionType}
					onChange={handleChange}
				>
					<ToggleButton value="dynamic">Relativo</ToggleButton>
					<ToggleButton value="fixed">Fixo</ToggleButton>
				</ToggleButtonGroup>
				<Box sx={{ flexGrow: 1, "& .MuiFormHelperText-root:empty": { marginTop: 0 }, "& .MuiSelect-icon": { right: 9 } }}>
					<DynamicTasksGroupBlock
						group={switcherGroup}
						item={item}
						type={type}
						entityForm={entityForm}
						groupsOptions={groupsOptions}
						hideTitle
					/>
				</Box>
			</Box>
			<DynamicTasksGroupBlock
				group={group}
				item={item}
				type={type}
				entityForm={entityForm}
				groupsOptions={restOptions}
				hideTitle
			/>
		</>
	);
};
